import api, {
  AbortMultipartDirectUploadOptions,
  CompleteMultipartDirectUploadOptions,
  CompleteMultipartDirectUploadResult,
  CreateEntireVideoInstanceArgs,
  CreateEntireVideoInstanceResult,
  GetEntireVideoByIdArgs,
  GetEntireVideoInstanceByIdArgs,
  InitMultipartDirectUploadResult,
  MediaContentInfo,
  ParsedExternalMedia,
  PreSignMultipartDirectUploadPartsOptions,
  PreSignMultipartDirectUploadPartsResult,
  SignedUploadBody,
} from '@sparemin/api-sdk';
import BluebirdPromise from 'bluebird';
import { normalize } from 'normalizr';

import { int } from 'utils/numbers';
import {
  entireVideoInstancesSchema,
  entireVideosSchema,
  mediaSegmentDataSchema,
  textSchema,
  videoLayoutTimelineSchema,
  videoSchema,
} from './schemas';
import * as types from './types';

const uploadVideo = ([
  file,
  transcribe,
  trimStart,
  trimEnd,
  lang,
  directUploadBucket,
  directUploadKey,
  initiateProcessing,
  providerUserId,
  googleDriveFileId,
  zoomMeetingId,
  zoomRecordingFileId,
  youtubeUrl,
]: types.UploadVideoArgs): BluebirdPromise<types.VideoNormalizationResult> => {
  return new BluebirdPromise((resolve, reject, onCancel) => {
    const abortController = new AbortController();

    onCancel?.(() => {
      abortController.abort();
    });

    api
      .uploadVideo(
        {
          directUploadBucket,
          directUploadKey,
          enableTranscription: transcribe,
          googleDriveFileId,
          initiateProcessing,
          language: lang,
          providerUserId,
          trimEndMillis: trimEnd,
          trimStartMillis: trimStart,
          videoFile: typeof file === 'string' ? undefined : file,
          videoUrl: typeof file === 'string' ? file : undefined,
          youtubeUrl,
          zoomMeetingId,
          zoomRecordingFileId,
        },
        {
          signal: abortController.signal,
        },
      )
      .then(res => resolve(normalize(res, videoSchema)))
      .catch(reject);
  });
};

async function getVideos(
  args: types.GetVideosArgs,
): Promise<types.GetVideosResult> {
  const [videoIds, includeVariations = false] = args;

  const result = await api.getVideoUploads({
    uploadedVideoId: videoIds,
    includeVariations,
  });

  return normalize(result.data, [videoSchema]);
}

const getVideo = async ([
  id,
]: types.GetVideoArgs): Promise<types.VideoNormalizationResult> => {
  const res = await api.getVideoUploadDetail({
    videoUploadId: typeof id === 'string' ? int(id) : id,
  });
  return normalize(res, videoSchema);
};

const searchVideo = async (args: types.SearchVideoArgs) =>
  api.searchVideoUploads(args);

const uploadText = async ([text, urlSummaryId]: types.UploadTextArgs) => {
  const res = await api.createTextBlob({
    urlSummaryId,
    textBlobBody: text,
  });
  return normalize(res, textSchema);
};

const getText = async ([
  id,
]: types.GetTextArgs): Promise<types.TextNormalizationResult> => {
  const res = await api.getTextBlobDetail({ textBlobId: id });
  return normalize(res, textSchema);
};

const convertGifToVideo = async ([source]: types.ConvertGifToVideoArgs) =>
  api.convertGifToVideo({
    gifUrl: typeof source === 'string' ? source : undefined,
    gifFile: typeof source === 'string' ? undefined : source,
  });

const parseExternalContent = async ([
  url,
]: types.ParseExternalContentArgs): Promise<ParsedExternalMedia> => {
  const { data } = await api.parseExternalMedia({ url });
  return data;
};

async function directUpload([
  fileType,
  contentType,
  filename,
]: types.DirectUploadArgs): Promise<SignedUploadBody> {
  return api.directUpload({
    fileType,
    filename,
    contentType,
  });
}

async function initMultipartDirectUpload([
  fileType,
  contentType,
  filename,
]: types.DirectUploadArgs): Promise<InitMultipartDirectUploadResult> {
  return api.initMultipartDirectUpload({ fileType, contentType, filename });
}

async function presignMultipartDirectUploadParts(
  args: PreSignMultipartDirectUploadPartsOptions,
): Promise<PreSignMultipartDirectUploadPartsResult> {
  return api.preSignMultipartDirectUploadParts(args);
}

async function completeMultipartDirectUpload(
  args: CompleteMultipartDirectUploadOptions,
): Promise<CompleteMultipartDirectUploadResult> {
  return api.completeMultipartDirectUpload(args);
}

async function abortMultipartDirectUpload(
  args: AbortMultipartDirectUploadOptions,
): Promise<void> {
  await api.abortMultipartDirectUpload(args);
}

async function getMediaContentInfo([
  url,
]: types.GetMediaContentInfoArgs): Promise<MediaContentInfo> {
  return api.getMediaContentInfo({ url });
}

async function getMediaSegmentDataByRevisionId({
  uuid,
  revisionId,
}: types.GetMediaSegmentDataByRevisionIdArgs): Promise<
  types.GetMediaSegmentDataByRevisionIdResult
> {
  const res = await api.getMediaSegmentDetailByRevisionId({ uuid, revisionId });
  return normalize(res, mediaSegmentDataSchema);
}

async function getVideoLayoutTimelineByRevisionId({
  uuid,
  revisionId,
}: types.GetVideoLayoutTimelineDataByRevisionIdArgs): Promise<
  types.GetVideoLayoutTimelineDataByRevisionIdResult
> {
  const res = await api.getVideoLayoutTimelineDetailByRevisionId({
    uuid,
    revisionId,
  });
  return normalize(res, videoLayoutTimelineSchema);
}

async function updateVideoLayoutTimelineByRevisionId(
  timeline: types.UpdateVideoLayoutTimelineByRevisionIdArgs,
) {
  const res = await api.updateVideoLayoutTimeline(timeline);

  return normalize(
    {
      ...res,
      data: timeline.data,
    },
    videoLayoutTimelineSchema,
  );
}

async function updateVideoLayoutTimelineSegment({
  uuid,
  revisionId,
  segmentId,
  segment,
  timeline,
}: types.UpdateVideoLayoutTimelineSegmentArgs) {
  const res = await api.updateVideoLayoutTimelineSegment({
    uuid,
    revisionId,
    segmentId,
    segment,
  });

  const updatedTimelineData = timeline.data.map(seg =>
    seg.segmentId === segment.segmentId ? segment : seg,
  );

  return normalize(
    {
      ...res,
      data: updatedTimelineData,
    },
    videoLayoutTimelineSchema,
  );
}

async function createEntireVideoInstance(
  args: CreateEntireVideoInstanceArgs,
): Promise<CreateEntireVideoInstanceResult> {
  const res = await api.createEntireVideoInstance(args);

  return res;
}

async function getEntireVideoInstanceById(
  args: GetEntireVideoInstanceByIdArgs,
): Promise<types.GetEntireVideoInstanceByIdResult> {
  const res = await api.getEntireVideoInstanceById(args);

  return normalize(
    res,
    entireVideoInstancesSchema,
  ) as types.GetEntireVideoInstanceByIdResult;
}

async function getEntireVideoById(
  args: GetEntireVideoByIdArgs,
): Promise<types.GetEntireVideoByIdResult> {
  const res = await api.getEntireVideoById(args);

  return normalize(res, entireVideosSchema) as types.GetEntireVideoByIdResult;
}

async function searchEntireVideo(
  args: types.SearchEntireVideoArgs,
): Promise<types.SearchEntireVideoResult> {
  const res = await api.searchEntireVideo(args);

  return normalize(res.data, [entireVideosSchema]);
}

export const handle = (method: any, args: any): any => {
  switch (method) {
    case types.ServiceMethod.UPLOAD_VIDEO:
      return uploadVideo(args);

    case types.ServiceMethod.GET_VIDEO:
      return getVideo(args);

    case types.ServiceMethod.GET_VIDEOS:
      return getVideos(args);

    case types.ServiceMethod.SEARCH_VIDEO:
      return searchVideo(args);

    case types.ServiceMethod.CONVERT_GIF_TO_VIDEO:
      return convertGifToVideo(args);

    case types.ServiceMethod.UPLOAD_TEXT:
      return uploadText(args);

    case types.ServiceMethod.GET_TEXT:
      return getText(args);

    case types.ServiceMethod.PARSE_EXTERNAL_CONTENT:
      return parseExternalContent(args);

    case types.ServiceMethod.DIRECT_UPLOAD:
      return directUpload(args);

    case types.ServiceMethod.MULTIPART_DIRECT_UPLOAD_INIT:
      return initMultipartDirectUpload(args);

    case types.ServiceMethod.MULTIPART_DIRECT_UPLOAD_PRESIGN:
      return presignMultipartDirectUploadParts(args);

    case types.ServiceMethod.MULTIPART_DIRECT_UPLOAD_COMPLETE:
      return completeMultipartDirectUpload(args);

    case types.ServiceMethod.MULTIPART_DIRECT_UPLOAD_ABORT:
      return abortMultipartDirectUpload(args);

    case types.ServiceMethod.GET_MEDIA_CONTENT_INFO:
      return getMediaContentInfo(args);

    case types.ServiceMethod.GET_MEDIA_SEGMENT_INFO_BY_REVISION_ID:
      return getMediaSegmentDataByRevisionId(args);

    case types.ServiceMethod.GET_VIDEO_LAYOUT_TIMELINE_BY_REVISION_ID:
      return getVideoLayoutTimelineByRevisionId(args);

    case types.ServiceMethod.UPDATE_VIDEO_LAYOUT_TIMELINE_BY_REVISION_ID:
      return updateVideoLayoutTimelineByRevisionId(args);

    case types.ServiceMethod.UPDATE_VIDEO_LAYOUT_TIMELINE_SEGMENT:
      return updateVideoLayoutTimelineSegment(args);

    case types.ServiceMethod.CREATE_ENTIRE_VIDEO_INSTANCE:
      return createEntireVideoInstance(args);

    case types.ServiceMethod.GET_ENTIRE_VIDEO_INSTANCE_BY_ID:
      return getEntireVideoInstanceById(args);

    case types.ServiceMethod.GET_ENTIRE_VIDEO_BY_ID:
      return getEntireVideoById(args);

    case types.ServiceMethod.SEARCH_ENTIRE_VIDEO:
      return searchEntireVideo(args);

    default:
      throw new Error(`${types.ACTION_KEY} cannot handle method ${method}`);
  }
};
